import "./LeftNavigationBar.css";

import { Link } from "react-router-dom";

import { ChevronDownIcon } from "./assets";
import { NavigationBarItem } from "./models";
import { useLeftNavigationBar } from "./useLeftNavigationBar";

interface LeftNavigationBarProps {
  /** Key of the currently active item or sub-item. */
  activeItem?: string;
  /** Key of the group expanded on first render (accordion: one open at a time). */
  defaultExpandedKey?: string;
}

export const LeftNavigationBar = ({ activeItem, defaultExpandedKey }: LeftNavigationBarProps): JSX.Element => {
  const { persona, items, navigationLogo, expandedKey, toggleGroup, isActive } = useLeftNavigationBar({
    activeItem,
    defaultExpandedKey,
  });

  const renderGroup = (item: NavigationBarItem): JSX.Element => {
    const isExpanded = expandedKey === item.key;
    const hasActiveChild = item.children?.some((child) => isActive(child.key)) ?? false;

    return (
      <div key={item.key} className="LeftNavigationBar_group">
        <button
          type="button"
          aria-expanded={isExpanded}
          className={`LeftNavigationBar_item LeftNavigationBar_item_button${
            isExpanded || hasActiveChild ? " LeftNavigationBar_item_active" : ""
          }`}
          onClick={() => toggleGroup(item.key)}
        >
          <span className="LeftNavigationBar_icon">{item.icon}</span>
          <span className="LeftNavigationBar_label">{item.label}</span>
          <ChevronDownIcon
            className={`LeftNavigationBar_chevron${isExpanded ? " LeftNavigationBar_chevron_open" : ""}`}
          />
        </button>

        {isExpanded && (
          <>
            <div className="LeftNavigationBar_subList">
              {item.children?.map((child) => (
                <Link
                  key={child.key}
                  to={child.route}
                  className={`LeftNavigationBar_subItem${
                    isActive(child.key) ? " LeftNavigationBar_subItem_active" : ""
                  }`}
                >
                  {child.label}
                </Link>
              ))}
            </div>
            <div className="LeftNavigationBar_divider" />
          </>
        )}
      </div>
    );
  };

  const renderLeaf = (item: NavigationBarItem): JSX.Element => (
    <Link
      key={item.key}
      to={item.route ?? "#"}
      className={`LeftNavigationBar_item${isActive(item.key) ? " LeftNavigationBar_item_active" : ""}`}
    >
      <span className="LeftNavigationBar_icon">{item.icon}</span>
      <span className="LeftNavigationBar_label">{item.label}</span>
    </Link>
  );

  return (
    <nav className={`LeftNavigationBar LeftNavigationBar_${persona}`}>
      <div className="LeftNavigationBar_brand">{navigationLogo}</div>
      <div className="LeftNavigationBar_items">
        {items.map((item) => (item.children?.length ? renderGroup(item) : renderLeaf(item)))}
      </div>
    </nav>
  );
};
